import React from 'react'
import {Link} from 'react-router-dom'
import {motion} from 'framer-motion'
import Layout from '../../components/layout/Layout'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'

const {FiFileText, FiArrowLeft, FiShield, FiUsers, FiHeart} = FiIcons

const TermsOfServicePage = () => {
  return (
    <Layout showNavbar={false}>
      <div className="min-h-screen bg-gradient-to-br from-neutral-bg via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-500 mb-6">
              <SafeIcon icon={FiArrowLeft} />
              Back to Home
            </Link>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiFileText} className="text-white text-xl" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Terms of Service</h1>
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                Last updated: January 2025
              </p>
            </motion.div>
          </div>

          {/* Content */}
          <Card className="prose prose-gray dark:prose-invert max-w-none">
            <div className="space-y-8">
              {/* Introduction */}
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <SafeIcon icon={FiHeart} className="text-primary-500" />
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 m-0">Welcome to Chore Haven</h2>
                </div>
                <p className="text-gray-700 dark:text-gray-300">
                  These Terms of Service ("Terms") govern your use of Chore Haven, a family chore management application 
                  ("Service") operated by Chore Haven ("us", "we", or "our"). By accessing or using our Service, you 
                  agree to be bound by these Terms.
                </p>
              </section>

              {/* Acceptance of Terms */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">1. Acceptance of Terms</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  By creating an account or using Chore Haven, you acknowledge that you have read, understood, and 
                  agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Service.
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  We may modify these Terms at any time. We will notify you of any changes by posting the new Terms 
                  on this page and updating the "Last updated" date. Your continued use of the Service after such 
                  changes constitutes acceptance of the new Terms.
                </p>
              </section>

              {/* Description of Service */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">2. Description of Service</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  Chore Haven is a web-based application designed to help families manage household chores and tasks 
                  through gamification. Our Service includes:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                  <li>Task creation and management</li>
                  <li>Point-based reward system</li>
                  <li>Progress tracking and analytics</li>
                  <li>Family member profiles and management</li>
                  <li>Calendar and scheduling features</li>
                  <li>Kiosk mode for child-friendly access</li>
                </ul>
              </section>

              {/* User Accounts */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">3. User Accounts</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  To use certain features of our Service, you must create an account. You are responsible for:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-3">
                  <li>Maintaining the confidentiality of your account credentials</li>
                  <li>All activities that occur under your account</li>
                  <li>Providing accurate and current information</li>
                  <li>Promptly notifying us of any unauthorized use</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300">
                  You must be at least 13 years old to create an account. If you are under 18, you must have 
                  parental consent to use our Service.
                </p>
              </section>

              {/* Family Management */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <SafeIcon icon={FiUsers} className="text-primary-500" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 m-0">4. Family Management & Child Safety</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  As a parent or guardian using Chore Haven:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-3">
                  <li>You are responsible for supervising your children's use of the Service</li>
                  <li>You control all data related to your family account</li>
                  <li>You may add, modify, or remove child profiles at any time</li>
                  <li>You are responsible for setting appropriate tasks and rewards</li>
                  <li>Kiosk mode should be used under appropriate supervision</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300">
                  We do not knowingly collect personal information from children under 13 without parental consent.
                </p>
              </section>

              {/* Acceptable Use */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">5. Acceptable Use</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  You agree to use our Service only for lawful purposes and in accordance with these Terms. 
                  You agree NOT to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                  <li>Use the Service for any illegal or unauthorized purpose</li>
                  <li>Violate any laws in your jurisdiction</li>
                  <li>Transmit any harmful, offensive, or inappropriate content</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with or disrupt the Service</li>
                  <li>Create tasks that could be harmful, dangerous, or inappropriate for children</li>
                </ul>
              </section>

              {/* Content and Data */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">6. Your Content and Data</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  You retain ownership of all content you create using our Service, including tasks, rewards, 
                  and family data. By using our Service, you grant us a limited license to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-3">
                  <li>Store and process your data to provide the Service</li>
                  <li>Create backups for data protection</li>
                  <li>Generate anonymized analytics to improve our Service</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300">
                  You may export or delete your data at any time through your account settings.
                </p>
              </section>

              {/* Privacy */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <SafeIcon icon={FiShield} className="text-primary-500" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 m-0">7. Privacy</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300">
                  Your privacy is important to us. Please review our Privacy Policy, which explains how we 
                  collect, use, and protect your information when you use our Service.
                </p>
              </section>

              {/* Service Availability */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">8. Service Availability</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  We strive to provide a reliable service, but we cannot guarantee that the Service will be 
                  available at all times. We may experience downtime for maintenance, updates, or unforeseen 
                  technical issues. We are not liable for any inconvenience caused by service interruptions.
                </p>
              </section>

              {/* Limitation of Liability */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">9. Limitation of Liability</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  To the fullest extent permitted by law, Chore Haven shall not be liable for any indirect, 
                  incidental, special, consequential, or punitive damages, or any loss of profits or revenues, 
                  whether incurred directly or indirectly, or any loss of data, use, goodwill, or other 
                  intangible losses resulting from your use of the Service.
                </p>
              </section>

              {/* Termination */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">10. Termination</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  You may terminate your account at any time by deleting it through your account settings. 
                  We may terminate or suspend your account immediately, without prior notice, if you breach 
                  these Terms.
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  Upon termination, your right to use the Service will cease immediately, and we may delete 
                  your account and data after a reasonable grace period.
                </p>
              </section>

              {/* Changes to Service */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">11. Changes to Service</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  We reserve the right to modify or discontinue the Service (or any part thereof) at any time 
                  with or without notice. We shall not be liable to you or any third party for any modification, 
                  suspension, or discontinuance of the Service.
                </p>
              </section>

              {/* Governing Law */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">12. Governing Law</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], 
                  without regard to its conflict of law provisions. Any disputes arising from these Terms or the 
                  Service shall be resolved in the courts of [Your Jurisdiction].
                </p>
              </section>

              {/* Contact Information */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">13. Contact Us</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <p className="text-gray-700 dark:text-gray-300 mb-2">
                    <strong>Email:</strong> legal@chorehaven.com
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 mb-2">
                    <strong>Website:</strong> https://chorehaven.com
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Response Time:</strong> We aim to respond within 48 hours
                  </p>
                </div>
              </section>

              {/* Footer Note */}
              <section className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  By using Chore Haven, you acknowledge that you have read and understood these Terms of Service 
                  and agree to be bound by them. Thank you for helping make chores fun for families everywhere! 🏠✨
                </p>
              </section>
            </div>
          </Card>

          {/* Navigation */}
          <div className="mt-8 text-center">
            <Link to="/">
              <Button variant="primary">
                Return to Chore Haven
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default TermsOfServicePage